import React from 'react';
import SEO from '../components/SEO';
import HardwareSolutions from '../components/HardwareSolutions';
import ContactCTA from '../components/ContactCTA';
import HardwareDevice from '../assets/images/Hardware-Device.png';
import MonitorIcon from '../assets/svg/monitor.svg';
import MeterIcon from '../assets/svg/meter.svg';
import VernIcon from '../assets/svg/vern.svg';
import ThumbsIcon from '../assets/svg/thumbsUp.svg';
import { useNavigate } from 'react-router-dom';
function HardwarePage() {
    const navigate = useNavigate();
    return (
        <div className="min-h-screen bg-white">
            <SEO
                title="POS Hardware Solutions | VernPOS Malaysia"
                description="Explore our range of POS hardware solutions. Choose from Landi C20 Pro, iMin D4 Pro, WizarPOS Q2, and WizarPOS Q8 - all fully compatible with VernPOS system."
                keywords="POS hardware Malaysia, POS terminal, POS device, Landi C20 Pro, iMin D4 Pro, WizarPOS, POS system hardware"
            />            {/* Hero Section */}
            <section className="relative min-h-screen overflow-hidden">
                {/* Two-tone Background */}
                <div className="absolute inset-0">
                    {/* Red Background - Top 70% */}
                    <div className="absolute top-0 left-0 right-0 h-[70%] bg-gradient-to-br bg-vern to-red-700"></div>
                    {/* White Background - Bottom 30% */}
                    <div className="absolute bottom-0 left-0 right-0 h-[30%] bg-white"></div>
                    {/* Smooth Curved Transition */}
                    <div className="absolute left-0 right-0" style={{ top: '100%', height: '10%' }}>
                        <svg
                            viewBox="0 0 1200 120"
                            className="w-full h-full"
                            preserveAspectRatio="none"
                        >
                            <path
                                d="M0,0 C400,80 800,80 1200,0 L1200,120 L0,120 Z"
                                fill="white"
                            />
                        </svg>
                    </div>
                </div>
                {/* Content Container */}
                <div className="relative z-10 container mx-auto max-w-6xl px-4 pt-32 pb-16">
                    {/* Centered Text Content */}
                    <div className="text-center mb-36">
                        <h1 className="text-5xl md:text-5xl lg:text-5xl font-bold text-white mb-6 leading-tight max-w-4xl mx-auto">
                            Power Your Business with High Performance POS Hardware
                        </h1>
                        <p className="text-lg md:text-xl text-white/90 leading-relaxed max-w-3xl mx-auto mb-">
                            Durable, efficient, and designed to keep your operations smooth—choose the best hardware that fits your business needs.
                        </p>
                    </div>
                    {/* POS Machine Image - Centered and Overlapping */}
                    <div className="relative flex justify-center">
                        <div className="relative z-20 transform hover:scale-105 transition-transform duration-300">
                            <img
                                src={HardwareDevice}
                                alt="Professional POS Hardware Device"
                                className="w-full max-w-3xl h-auto object-contain drop-shadow-2xl"
                                style={{
                                    filter: 'drop-shadow(0 20px 40px rgba(0,0,0,0.3)) drop-shadow(0 0 20px rgba(220,38,38,0.2))',
                                    marginTop: '-100px' // Overlap with navbar
                                }}
                            />
                        </div>
                        {/* Subtle Glow Effect */}
                        <div
                            className="absolute inset-0 z-10 blur-3xl opacity-30"
                            style={{
                                background: 'radial-gradient(ellipse at center, rgba(220,38,38,0.4) 0%, transparent 70%)',
                                transform: 'scale(1.2)'
                            }}
                        ></div>
                    </div>
                </div>
                {/* Additional Decorative Elements */}
                <div className="absolute top-1/4 left-10 w-20 h-20 bg-white/10 rounded-full blur-xl"></div>
                <div className="absolute top-1/3 right-16 w-32 h-32 bg-red-400/20 rounded-full blur-2xl"></div>
                <div className="absolute bottom-1/4 left-1/4 w-24 h-24 bg-white/5 rounded-full blur-lg"></div>
            </section>
            {/* Built for Reliability Section */}
            <section className="py-16 px-4 bg-white">
                <div className="container mx-auto max-w-6xl">
                    <div className="text-center mb-12">
                        <h2 className="text-3xl md:text-6xl font-bold text-black mb-4">
                            Built for Reliability.<br />
                            Trusted by Professionals.
                        </h2>
                        <p className="text-lg text-gray-700 max-w-3xl mx-auto">
                            From bustling restaurants to busy retail counters, our POS hardware delivers uncompromising performance where it matters most.
                        </p>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-8">
                        <div className="flex items-start p-6 bg-[#f6f6f7] rounded-3xl">
                            <div className="bg-[#FCF4F3] rounded-full w-16 h-16 flex items-center justify-center mr-4 flex-shrink-0 my-6">
                                <img src={MonitorIcon} alt="Monitor Icon" className="w-8 h-8 text-vern" />
                            </div>
                            <div>
                                <h3 className="text-xl font-bold text-black mb-1 my-2">Modern & Sleek Design</h3>
                                <p className="text-gray-600 font-medium">Fits seamlessly into any counter space with a clean, professional look that enhances your setup.</p>
                            </div>
                        </div>
                        <div className="flex items-start p-6 bg-[#f6f6f7] rounded-3xl">
                            <div className="bg-[#FCF4F3] rounded-full w-16 h-16 flex items-center justify-center mr-4 flex-shrink-0 my-6">
                                <img src={MeterIcon} alt="Monitor Icon" className="w-8 h-8 text-vern" />
                            </div>
                            <div>
                                <h3 className="text-xl font-bold text-black mb-2">Lightning-Fast Performance</h3>
                                <p className="text-gray-600 font-medium">Handle peak hours effortlessly with powerful, high-speed hardware built for smooth performance all day long.</p>
                            </div>
                        </div>
                        <div className="flex items-start p-6 bg-[#f6f6f7] rounded-3xl">
                            <div className="bg-[#FCF4F3] rounded-full w-16 h-16 flex items-center justify-center mr-4 flex-shrink-0 my-6">
                                <img src={VernIcon} alt="Monitor Icon" className="w-8 h-8 text-vern" />
                            </div>
                            <div>
                                <h3 className="text-xl font-bold text-black mb-2">VernPOS Compatible</h3>
                                <p className="text-gray-600 font-medium">Fully optimized for seamless integration with VernPOS—complete setup or configuration needed.</p>
                            </div>
                        </div>
                        <div className="flex items-start p-6 bg-[#f6f6f7] rounded-3xl">
                            <div className="bg-[#FCF4F3] rounded-full w-16 h-16 flex items-center justify-center mr-4 flex-shrink-0 my-6">
                                <img src={ThumbsIcon} alt="Monitor Icon" className="w-8 h-8 text-vern" />
                            </div>
                            <div>
                                <h3 className="text-xl font-bold text-black mb-2">Built to Last</h3>
                                <p className="text-gray-600 font-medium">Crafted with premium materials to ensure durability and long-term reliability in high-demand environments.</p>
                            </div>
                        </div>
                    </div>
                    <div className="text-center mt-12">
                        <button onClick={() => { navigate('/contact-us'); }} className="bg-vern hover:bg-red-700 text-white font-semibold py-3 px-8 rounded-full transition-colors duration-300 inline-block transition-transform hover:scale-105">
                            Book a Demo
                        </button>
                    </div>
                </div>
            </section>
            {/* Hardware Solutions */}
            <HardwareSolutions />
            {/* Contact CTA */}
            <div className="max-w-6xl mx-auto px-4 py-8">
                <ContactCTA />
            </div>
        </div>
    );
}
export default HardwarePage;